import { useEffect, useState } from 'react';
import ButtonComponent from '../../components/button/button';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { getSearchResults } from '../../features/search/service/search-service';
import DatePickerComponent from '../../components/input/input-date';
import { ExportDelayData } from './mapper/delay-mapper';
import { getMe } from '../../features/search/service/user-service';
import { getRoleById } from '../../features/search/service/role-service';
import {
    getCompanyById,
    getSuperAdminCompaniesDashboard,
    getSuperAdminCompanyById
} from '../../features/search/service/company-service';
import { User } from '../../shared/types/user';
import { Role } from '../../shared/types/role';
import { Company } from '../../shared/types/company';
import { SelectOption } from '../../shared/types/select-options';
import SelectComponent from '../../components/input/select';
import { useTranslation } from '../../shared/i18n/i18n-context';

const Reports = () => {
    const navigate = useNavigate();
    const { language, t } = useTranslation('reports');
    const [initialDate, setInitialDate] = useState<Dayjs | null>(null);
    const [finalDate, setFinalDate] = useState<Dayjs | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isFillingPeriod, setIsFillingPeriod] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [userRole, setUserRole] = useState<Role | null>(null);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [companyDetails, setCompanyDetails] = useState<Company[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [loadingData, setLoadingData] = useState(true);

    const isSuperAdmin = userRole?.name === 'super-admin';

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoadingData(true);
                
                const userData = await getMe();
                setUser(userData);

                // Buscar a role do usuário
                let roleData: Role | null = null;
                if (userData.role_id) {
                    roleData = await getRoleById(userData.role_id);
                    setUserRole(roleData);
                }

                // Se for super-admin, buscar todas as empresas
                if (roleData?.name === 'super-admin') {
                    const companiesResponse = await getSuperAdminCompaniesDashboard();
                    const companiesData = companiesResponse.data;
                    setCompanyDetails(companiesData);
                    setCompanies(companiesData.map((company) => ({
                        id: company.id,
                        label: company.name || company.description,
                    })));
                    
                    // Selecionar a primeira empresa por padrão
                    if (companiesData.length > 0) {
                        setSelectedCompanyId(String(companiesData[0].id));
                    }
                } else {
                    // Se não for super-admin, usar a empresa do usuário
                    const companyId = userData.company?.id || userData.companyId;
                    if (companyId) {
                        setSelectedCompanyId(companyId);
                    }
                }

            } catch (error) {
                console.error('❌ Erro ao buscar dados:', error);
                alert(t('loadError'));
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, [t]);

    const getCompanyCreatedAt = (company?: Company | User['company'] | null) => {
        return company?.created_at || company?.createdAt;
    };

    const getSelectedCompanyCreatedAt = async () => {
        if (isSuperAdmin) {
            const selectedCompany = companyDetails.find((company) => String(company.id) === selectedCompanyId);
            const selectedCompanyCreatedAt = getCompanyCreatedAt(selectedCompany);

            if (selectedCompanyCreatedAt) {
                return selectedCompanyCreatedAt;
            }

            const company = await getSuperAdminCompanyById(selectedCompanyId);
            return getCompanyCreatedAt(company);
        }

        const userCompanyCreatedAt = getCompanyCreatedAt(user?.company);

        if (userCompanyCreatedAt) {
            return userCompanyCreatedAt;
        }

        const company = await getCompanyById(selectedCompanyId);
        return getCompanyCreatedAt(company);
    };

    const handleFillFullPeriod = async () => {
        if (!selectedCompanyId) {
            alert(t('selectCompanyFullPeriod'));
            return;
        }

        try {
            setIsFillingPeriod(true);

            const companyCreatedAt = await getSelectedCompanyCreatedAt();

            if (!companyCreatedAt) {
                alert(t('companyCreationDateMissing'));
                return;
            }

            const companyCreatedDate = dayjs(companyCreatedAt);

            if (!companyCreatedDate.isValid()) {
                alert(t('companyCreationDateInvalid'));
                return;
            }

            setInitialDate(companyCreatedDate.startOf('day'));
            setFinalDate(dayjs());
        } catch (error) {
            alert(t('fillFullPeriodError'));
        } finally {
            setIsFillingPeriod(false);
        }
    };

    const handleDownloadReport = async () => {
        if (!selectedCompanyId) {
            console.error('❌ Nenhuma empresa selecionada');
            alert(t('selectCompanyReport'));
            return;
        }

        try {
            setIsLoading(true);
            
            const queryParams = new URLSearchParams();
            queryParams.append('companyId', selectedCompanyId);
            
            if (initialDate) {
                const formattedStart = initialDate.format('YYYY-MM-DD');
                queryParams.append('startDate', formattedStart);
            }
            
            if (finalDate) {
                const formattedEnd = finalDate.format('YYYY-MM-DD');
                queryParams.append('endDate', formattedEnd);
            }
            
            const response = await getSearchResults(queryParams);
            
            if (response.data && response.data.length > 0) {
                // Buscar nome da empresa para o arquivo
                let companyName = t('defaultCompanyFileName');
                
                if (isSuperAdmin) {
                    const selectedCompany = companies.find(c => String(c.id) === selectedCompanyId);
                    companyName = selectedCompany?.label || t('defaultCompanyFileName');
                } else {
                    companyName = user?.company?.name || t('defaultCompanyFileName');
                }
                
                const safeCompanyName = companyName.replace(/\s+/g, '_').toLowerCase();
                const fileName = `${t('reportFilePrefix')}_${safeCompanyName}`;
                
                ExportDelayData(response, fileName, language);
                
                alert(`✅ ${t('reportSuccess')}\n${response.data.length} ${t('recordsFound')}`);
            } else {
                alert(t('noRecordsFound'));
            }
        } catch (error) {
            alert(t('reportError'));
        } finally {
            setIsLoading(false);
        }
    };

    if (loadingData) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <p style={{ color: '#3a8d8e', fontFamily: 'Roboto, sans-serif' }}>{t('loading')}</p>
            </div>
        );
    }
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
            <img src="../../../assets/logo-inteira.png" alt="Logo delay discounting" className="logo" style={{ width: '300px', height: '200px' }}/>
            {isSuperAdmin && (
                <h1 
                style={{
                    textAlign: 'center', 
                    fontSize: '2rem', 
                    fontFamily: 'Roboto, sans-serif', 
                    color: '#3a8d8e',
                    margin: '1rem 0',
                }}>
                {t('selectCompanyAndPeriod')}
            </h1>
            )}

            {/* Select de empresa - só aparece para super-admin */}
            {isSuperAdmin && companies.length > 0 && (
                <div style={{ width: '300px', margin: '1rem 0' }}>
                    <SelectComponent
                        label={t('company')}
                        value={selectedCompanyId}
                        onChange={(value) => setSelectedCompanyId(String(value))}
                        options={companies}
                    />
                </div>
            )}

            <h2 style={{ textAlign: 'center', fontSize: '1.5rem', fontFamily: 'Roboto, sans-serif', color: '#3a8d8e', margin: '1rem 0' }}>
                {t('selectPeriod')}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', margin: '1rem' }}>
                <DatePickerComponent 
                    label={t('startDate')}
                    value={initialDate}
                    onChange={(event: Dayjs | null) => setInitialDate(event)}
                    styles={{
                        margin: '0 1rem',
                    }}
                />
                
                <DatePickerComponent 
                    label={t('endDate')}
                    value={finalDate}
                    onChange={(event: Dayjs | null) => setFinalDate(event)}
                />
            </div>

            <ButtonComponent
                label={isFillingPeriod ? t('loadingPeriod') : t('fullPeriod')}
                onClick={() => handleFillFullPeriod()}
                props={{
                    disabled: isLoading || isFillingPeriod || !selectedCompanyId,
                }}
            />

            <ButtonComponent 
                label={isLoading ? t('generating') : t('downloadReport')} 
                onClick={() => handleDownloadReport()}
                props={{
                    disabled: !initialDate || !finalDate || isLoading || isFillingPeriod || !selectedCompanyId,
                }}
            />
            <ButtonComponent 
                isReturnButton={true} 
                label={t('backHome')} 
                onClick={() => navigate('/home')} 
                props={{
                    disabled: isLoading || isFillingPeriod,
                }}
            />
        </div>
    );
}

export default Reports;
